/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Row,
  Tabs,
  Form,
  Col,
  Input,
  Spin,
  Card,
  DatePicker,
} from 'antd';
import {
  deleteMercado,
  getMercados,
  getMercado,
  patchMercado,
  postMercado,
} from 'api/empresas/mercado';
import {
  EnvironmentOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import { getModelPermission } from 'utils/permission/index';
import Direccion from 'components/Direccion';
import makeMessager from 'utils/complexFormsMessages';
import { onError } from 'utils/handlers';
import address from 'utils/address';
import Select from 'components/Select';
import { getTiposDeMercado } from 'api/catalogos/tiposDeMercado';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';

const { TabPane } = Tabs;
const baseURI = 'empresas/mercados/';

// eslint-disable-next-line react/prop-types
const Mercado = ({ permission }) => {
  const addressPermission = useMemo(() => getModelPermission('direccion'), []);
  const [form] = Form.useForm();
  const [formInfo] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [formAddress, setFormAddress] = useState();
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [tipoDeMercado, setTipoDeMercado] = useState([]);

  const basicKeys = ['general'];

  const complexForms = [{ key: 'direccion', formInstance: formAddress, permission: addressPermission }];

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const _data = await getMercados({
      ...q,
      page,
    });
    setData(_data);
    setLoading(false);
  };
  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const [_data, _tipoDeMercado] = await Promise.all([
        getMercados({ page: 1 }, true),
        getTiposDeMercado(),
      ]);
      if (mounted) {
        setData(_data);
        setTipoDeMercado(_tipoDeMercado);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setCurrentTabKey('general');
  };

  const onClickEdit = async (mercadoId) => {
    const key = selectedRowKeys[0] || mercadoId;
    const match = await getMercado(key);
    form.setFieldsValue(match);
    setVisible(true);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formInfo.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      const _data = await (key ? patchMercado(key, values) : postMercado(values));
      if (_data) {
        await fetchData();
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([_data.id]);
          }
          await onClickEdit(_data.id);
        } else {
          onCancel();
        }
        setLoading(false);
        return _data;
      }
      setLoading(false);
      return true;
    } catch (err) {
      onError(err, setLoading, [form, formInfo], setCurrentTabKey);
      return false;
    }
  };
  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);
  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteMercado(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys, complexForms, currentTabKey, setCurrentTabKey, showMsg,
  );
  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 150,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 200,
    },
    {
      titleText: 'Tipo de Mercado',
      key: 'tipo_de_mercado',
      dataIndex: 'tipo_de_mercado',
      width: 200,
      catalog: {
        cat: tipoDeMercado,
      },
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 200,
    },
    {
      titleText: 'Dirección',
      dataIndex: 'direccion',
      key: 'direccion',
      width: 300,
      render: (val) => address(val),
    },
  ];
  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible
          ? (
            <>
              <Table
                cols={columns}
                mobileColumns={[columns[0]]}
                data={data?.results}
                rowSelection={rowSelection}
                handleOnRowClick={handleOnRowClick}
                controls={{
                  onClickAdd,
                  onClickEdit,
                  onClickDelete,
                }}
                rowKey="id"
                baseURI={baseURI}
                permission={permission}
                pagination={false}
                paginationCallback={fetchData}
                count={data.count}
                currentPage={currentPage}
              />
            </>
          ) : (
            <Card
              className="form-container"
              title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Mercado`}
              extra={(
                <FormSubmitControls
                  onFinish={getOnFinish()}
                  onCancel={onCancel}
                  allowSaveAndContinue
                  onCancelText="Cancelar"
                  permission={permission}
                />
              )}
              bordered={false}
            >
              <Tabs
                onChange={onChangeTabKey}
                activeKey={currentTabKey}
              >
                <TabPane
                  tab={(
                    <span>
                      <InfoCircleOutlined />
                      Información General
                    </span>
                  )}
                  key="general"
                  forceRender
                >
                  <Form
                    name="general"
                    layout="vertical"
                    form={form}
                    onFinish={() => onFinish()}
                    scrollToFirstError
                  >
                    <Row gutter={10}>
                      { selectedRowKeys && (
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="clave"
                            label="Clave"
                            hasFeedback
                            required
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                      )}
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="descripcion"
                          label="Descripcion"
                          hasFeedback
                          required
                        >
                          <Input allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="tipo_de_mercado"
                          label="Tipo de Mercado"
                          hasFeedback
                        >
                          <Select dataSource={tipoDeMercado} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="fecha_de_alta"
                          label="Fecha de Alta"
                          hasFeedback
                        >
                          <DatePicker
                            format={DATE_FRONTEND_FORMAT}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={16}>
                        <Form.Item
                          name="observaciones"
                          label="Observaciones"
                          hasFeedback
                        >
                          <Input.TextArea autoSize={{ minRows: 3, maxRows: 3 }} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </TabPane>
                <TabPane
                  tab={(
                    <span>
                      <EnvironmentOutlined />
                      Direccion
                    </span>
                  )}
                  key="direccion"
                  forceRender
                  disabled={!selectedRowKeys.length}
                >
                  <Direccion
                    currentURI={baseURI}
                    parentID={selectedRowKeys[0]}
                    setLoading={setLoading}
                    setFormAddress={setFormAddress}
                    setCurrentTabKey={setCurrentTabKey}
                    addressPropName="direccion"
                    callback={(values, _continue) => {
                      if (!_continue) {
                        onCancel();
                      } else {
                        const [key] = selectedRowKeys;
                        const clone = [...data];
                        const mappedData = clone.map((e) => (e.id === key
                          ? ({ ...e, direccion: values?.id }) : e));
                        setData(mappedData);
                      }
                    }}
                    permission={addressPermission}
                  />
                </TabPane>
              </Tabs>
            </Card>
          )}
      </Spin>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content="Mercado seleccionado"
        loading={loading}
      />
    </Row>
  );
};

export default Mercado;
